import Phaser from 'phaser';
import { SceneKeys } from '../game/Constants';
import { GameState } from '../game/GameState';
import { ArmyManager } from '../systems/ArmyManager';
import { AssetRegistry } from '../systems/AssetRegistry';
import { AudioManager } from '../systems/AudioManager';
import { DataRegistry } from '../systems/DataRegistry';
import { EventManager } from '../systems/EventManager';
import { ProvinceManager } from '../systems/ProvinceManager';
import { RecruitmentManager } from '../systems/RecruitmentManager';
import { SaveManager } from '../systems/SaveManager';
import { SupplyManager } from '../systems/SupplyManager';
import { TurnManager } from '../systems/TurnManager';
import type { ProvinceData } from '../types/data';
import type { ArmyState, CampaignMapMode, GameLogEntry } from '../types/game';
import { VisualTheme } from '../ui/VisualTheme';
import { createTextButton } from '../ui/components/UIButton';
import { formatMonth } from '../utils/math';

const MAP_MODES: Array<{ id: CampaignMapMode; label: string; description: string }> = [
  { id: 'political', label: 'Political', description: 'Ownership, occupation and diplomatic borders.' },
  { id: 'terrain', label: 'Terrain', description: 'Plains, coast, highlands, forest and island terrain.' },
  { id: 'religion', label: 'Religion', description: 'The dominant faith practiced in every province.' },
  { id: 'culture', label: 'Culture', description: 'Regional cultures and the identities of local elites.' },
  { id: 'trade', label: 'Trade', description: 'Ports, deposits and commercially important provinces.' },
  { id: 'unrest', label: 'Unrest', description: 'Public order, resistance and provinces at risk of revolt.' },
  { id: 'supply', label: 'Supply', description: 'Supply depots and the capacity to sustain campaigning armies.' },
  { id: 'development', label: 'Development', description: 'Population, prosperity and developed provincial centers.' },
  { id: 'devastation', label: 'Damage', description: 'War damage, looting and long-term provincial devastation.' },
];

interface TurnSummary {
  title: string;
  detail: string;
  level: GameLogEntry['level'];
}

interface ProjectionBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

export class CampaignMapScene extends Phaser.Scene {
  private lastProvinceClick: Record<string, number> = {};
  private tooltip?: Phaser.GameObjects.Container;
  private turnSummary?: TurnSummary;
  private endingTurn = false;
  private projectionBounds?: ProjectionBounds;
  private cursors?: Phaser.Types.Input.Keyboard.CursorKeys;
  private panKeys?: { left: Phaser.Input.Keyboard.Key; right: Phaser.Input.Keyboard.Key; up: Phaser.Input.Keyboard.Key; down: Phaser.Input.Keyboard.Key };

  constructor() {
    super(SceneKeys.CampaignMap);
  }

  create() {
    AudioManager.instance.playMusic(this, 'ambient_strategic_map');
    this.input.mouse?.disableContextMenu();
    this.configureCamera();
    this.render();
  }

  private configureCamera() {
    this.cameras.main.setBounds(0, 0, 1600, 900).setZoom(1);
    this.cursors = this.input.keyboard?.createCursorKeys();
    this.panKeys = this.input.keyboard
      ? {
          left: this.input.keyboard.addKey('A'),
          right: this.input.keyboard.addKey('D'),
          up: this.input.keyboard.addKey('W'),
          down: this.input.keyboard.addKey('S'),
        }
      : undefined;

    this.input.on('wheel', (pointer: Phaser.Input.Pointer, _gameObjects: unknown, _dx: number, dy: number) => {
      if (pointer.x > 1080 || pointer.y < 160) return;
      this.cameras.main.zoom = Phaser.Math.Clamp(this.cameras.main.zoom + (dy > 0 ? -0.06 : 0.06), 0.82, 1.25);
    });
    this.input.keyboard?.on('keydown-F', () => {
      if (this.scale.isFullscreen) this.scale.stopFullscreen();
      else this.scale.startFullscreen();
    });
    this.input.keyboard?.on('keydown-HOME', () => {
      this.cameras.main.setZoom(1).centerOn(800, 450);
    });
    this.input.keyboard?.on('keydown-M', () => this.cycleMapMode());
    MAP_MODES.forEach((mode, index) => {
      this.input.keyboard?.on(`keydown-${index + 1}`, () => {
        GameState.instance.requireCampaign().activeOverlay = mode.id;
        this.render();
      });
    });
  }

  private render() {
    this.hideTooltip();
    this.children.removeAll();
    const campaign = GameState.instance.requireCampaign();
    campaign.selectedProvinceId ??= Object.keys(campaign.provinces)[0];
    if (campaign.selectedArmyId && !campaign.armies[campaign.selectedArmyId]) campaign.selectedArmyId = undefined;

    this.drawBackdrop();
    this.drawWorld();
    this.drawTopBar();
    this.drawNavigation();
    this.drawSidePanel();
    this.drawNotifications();
    this.drawLegend();
    this.drawTurnSummary();

    const exposed = window as unknown as { render_game_to_text?: () => string; advanceTime?: (ms: number) => void };
    exposed.render_game_to_text = () => GameState.instance.renderToText();
    exposed.advanceTime = () => undefined;
  }

  override update(_time: number, delta: number) {
    const camera = this.cameras.main;
    const speed = 0.35 * delta / camera.zoom;
    if (this.panKeys?.left.isDown || this.cursors?.left.isDown) camera.scrollX -= speed;
    if (this.panKeys?.right.isDown || this.cursors?.right.isDown) camera.scrollX += speed;
    if (this.panKeys?.up.isDown || this.cursors?.up.isDown) camera.scrollY -= speed;
    if (this.panKeys?.down.isDown || this.cursors?.down.isDown) camera.scrollY += speed;
  }

  private drawBackdrop() {
    this.add.rectangle(800, 450, 1700, 1000, VisualTheme.colors.ink);
    this.add.rectangle(530, 470, 1040, 610, VisualTheme.colors.ocean, 0.98).setStrokeStyle(4, VisualTheme.colors.gold, 0.8).setDepth(0);
  }

  private drawWorld() {
    const registry = DataRegistry.instance;
    const campaign = GameState.instance.requireCampaign();
    const mapKey = AssetRegistry.instance.image('strategic_map_java_base', 'fallback.strategic-map');
    this.add.image(530, 470, this.textures.exists(mapKey) ? mapKey : 'fallback.strategic-map')
      .setDisplaySize(1018, 588)
      .setAlpha(0.84)
      .setDepth(1);
    this.add.rectangle(530, 470, 1018, 588, VisualTheme.colors.ink, 0.18).setDepth(2);

    const connections = this.add.graphics().setDepth(3);
    connections.lineStyle(5, 0x10222c, 0.78);
    const seenLinks = new Set<string>();
    for (const province of registry.provinceList()) {
      const from = this.projectProvince(province);
      for (const neighborId of province.neighbors) {
        const key = [province.id, neighborId].sort().join(':');
        if (seenLinks.has(key)) continue;
        seenLinks.add(key);
        const neighbor = registry.provinces[neighborId];
        if (!neighbor) continue;
        const to = this.projectProvince(neighbor);
        connections.lineBetween(from.x, from.y, to.x, to.y);
      }
    }
    connections.lineStyle(2, VisualTheme.colors.parchment, 0.28);
    seenLinks.clear();
    for (const province of registry.provinceList()) {
      const from = this.projectProvince(province);
      for (const neighborId of province.neighbors) {
        const key = [province.id, neighborId].sort().join(':');
        if (seenLinks.has(key)) continue;
        seenLinks.add(key);
        const neighbor = registry.provinces[neighborId];
        if (!neighbor) continue;
        const to = this.projectProvince(neighbor);
        connections.lineBetween(from.x, from.y, to.x, to.y);
      }
    }

    for (const province of registry.provinceList()) this.drawProvince(province);
    this.drawArmyRoutes();
    this.drawArmies();

    this.add.text(34, 746, 'WASD / arrows pan  •  Mouse wheel zoom  •  Home resets view  •  Right-click a province to march', {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '13px',
      color: VisualTheme.text.muted,
      stroke: '#000000',
      strokeThickness: 2,
    }).setDepth(50);
  }

  private drawProvince(province: ProvinceData) {
    const registry = DataRegistry.instance;
    const campaign = GameState.instance.requireCampaign();
    const provinceState = campaign.provinces[province.id];
    if (!provinceState) return;
    const owner = registry.factions[provinceState.owner];
    const position = this.projectProvince(province);
    const radius = this.provinceRadius(province);
    const points = this.provincePolygon(province.id, radius);
    const color = this.overlayColor(province.id);
    const selected = campaign.selectedProvinceId === province.id;

    const region = this.add.polygon(position.x, position.y, points, color, selected ? 0.86 : 0.69)
      .setStrokeStyle(selected ? 5 : 2, selected ? VisualTheme.colors.goldBright : 0xe8dcc4, selected ? 1 : 0.55)
      .setDepth(selected ? 8 : 5);
    region.setInteractive(new Phaser.Geom.Polygon(points), Phaser.Geom.Polygon.Contains);
    if (region.input) region.input.cursor = 'pointer';

    region.on('pointerover', (pointer: Phaser.Input.Pointer) => {
      region.setFillStyle(this.brightenColor(color, 28), 0.92);
      region.setStrokeStyle(4, VisualTheme.colors.goldBright, 1).setDepth(9);
      this.showProvinceTooltip(pointer, province.id);
    });
    region.on('pointermove', (pointer: Phaser.Input.Pointer) => this.moveTooltip(pointer));
    region.on('pointerout', () => {
      region.setFillStyle(color, selected ? 0.86 : 0.69);
      region.setStrokeStyle(selected ? 5 : 2, selected ? VisualTheme.colors.goldBright : 0xe8dcc4, selected ? 1 : 0.55).setDepth(selected ? 8 : 5);
      this.hideTooltip();
    });
    region.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.rightButtonDown()) {
        this.moveSelectedArmy(province.id);
        return;
      }
      const now = this.time.now;
      if ((this.lastProvinceClick[province.id] ?? 0) + 350 > now) {
        ProvinceManager.instance.selectProvince(province.id);
        this.scene.start(SceneKeys.Province);
        return;
      }
      this.lastProvinceClick[province.id] = now;
      ProvinceManager.instance.selectProvince(province.id);
      this.render();
    });

    const ownerColor = Phaser.Display.Color.HexStringToColor(owner?.colors.primary ?? '#777777').color;
    this.add.circle(position.x - radius * 0.53, position.y - radius * 0.48, 9, ownerColor, 1)
      .setStrokeStyle(2, VisualTheme.colors.parchment, 0.9)
      .setDepth(12);
    if (province.port) {
      this.add.circle(position.x + radius * 0.5, position.y - radius * 0.4, 8, 0x2d8ca1, 1)
        .setStrokeStyle(2, 0xd9f4ff, 0.9)
        .setDepth(12);
      this.add.text(position.x + radius * 0.5, position.y - radius * 0.4, 'P', {
        fontFamily: VisualTheme.fonts.body,
        fontSize: '9px',
        color: '#ffffff',
      }).setOrigin(0.5).setDepth(13);
    }
    if ((provinceState.fortLevel ?? 0) > 0) {
      this.add.rectangle(position.x + radius * 0.48, position.y + radius * 0.38, 15, 15, 0x6b5134, 1)
        .setStrokeStyle(2, VisualTheme.colors.gold, 1)
        .setDepth(12);
    }

    this.add.text(position.x, position.y + radius + 9, province.name, {
      fontFamily: VisualTheme.fonts.display,
      fontSize: selected ? '16px' : '14px',
      color: selected ? VisualTheme.text.title : '#ffffff',
      stroke: '#000000',
      strokeThickness: 4,
    }).setOrigin(0.5).setDepth(14);

    const modeValue = this.provinceModeValue(province.id);
    this.add.text(position.x, position.y + 1, modeValue, {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '11px',
      color: owner?.colors.secondary ?? '#ffffff',
      stroke: '#000000',
      strokeThickness: 3,
    }).setOrigin(0.5).setDepth(14);
  }

  private drawArmyRoutes() {
    const campaign = GameState.instance.requireCampaign();
    const routes = this.add.graphics().setDepth(18);
    for (const army of Object.values(campaign.armies)) {
      if (!army.destinationProvinceId) continue;
      const path = ArmyManager.instance.plannedPath(army.id);
      if (path.length < 2) continue;
      const isSelected = campaign.selectedArmyId === army.id;
      const isPlayer = army.factionId === campaign.playerFactionId;
      if (!isSelected && !isPlayer) continue;
      const faction = DataRegistry.instance.factions[army.factionId];
      const color = Phaser.Display.Color.HexStringToColor(faction?.colors.accent ?? (isPlayer ? '#f3c86a' : '#df6c65')).color;
      routes.lineStyle(isSelected ? 5 : 3, color, isSelected ? 0.95 : 0.55);
      for (let index = 0; index < path.length - 1; index++) {
        const fromProvince = DataRegistry.instance.provinces[path[index]];
        const toProvince = DataRegistry.instance.provinces[path[index + 1]];
        if (!fromProvince || !toProvince) continue;
        const from = this.projectProvince(fromProvince);
        const to = this.projectProvince(toProvince);
        this.drawDashedLine(routes, from.x, from.y, to.x, to.y, 13, 8);
        const angle = Phaser.Math.Angle.Between(from.x, from.y, to.x, to.y);
        const arrowX = Phaser.Math.Linear(from.x, to.x, 0.72);
        const arrowY = Phaser.Math.Linear(from.y, to.y, 0.72);
        this.add.triangle(arrowX, arrowY, -8, -6, 9, 0, -8, 6, color, isSelected ? 0.95 : 0.65)
          .setRotation(angle)
          .setDepth(19);
      }
      const destination = DataRegistry.instance.provinces[path[path.length - 1]];
      if (destination) {
        const destinationPosition = this.projectProvince(destination);
        this.add.text(destinationPosition.x, destinationPosition.y - 58, `ETA ${path.length - 1}m`, {
          fontFamily: VisualTheme.fonts.body,
          fontSize: '12px',
          color: '#fff0c8',
          backgroundColor: '#111822dd',
          padding: { x: 6, y: 3 },
        }).setOrigin(0.5).setDepth(22);
      }
    }
  }

  private drawArmies() {
    const campaign = GameState.instance.requireCampaign();
    const registry = DataRegistry.instance;
    const armiesByProvince = new Map<string, ArmyState[]>();
    for (const army of Object.values(campaign.armies)) {
      armiesByProvince.set(army.provinceId, [...(armiesByProvince.get(army.provinceId) ?? []), army]);
    }

    for (const [provinceId, armies] of armiesByProvince) {
      const province = registry.provinces[provinceId];
      if (!province) continue;
      const position = this.projectProvince(province);
      armies.forEach((army, index) => {
        const isPlayer = army.factionId === campaign.playerFactionId;
        const selected = campaign.selectedArmyId === army.id;
        const faction = registry.factions[army.factionId];
        const factionColor = Phaser.Display.Color.HexStringToColor(faction?.colors.primary ?? '#777777').color;
        const offsetX = 30 + (index % 2) * 48;
        const offsetY = -32 + Math.floor(index / 2) * 48;
        const ring = this.add.circle(0, 0, selected ? 28 : 25, selected ? VisualTheme.colors.goldBright : 0x080b0f, selected ? 0.92 : 0.8);
        const shield = this.add.circle(0, 0, 21, factionColor, 1).setStrokeStyle(3, isPlayer ? VisualTheme.colors.goldBright : 0xe4d8c0, 1);
        const banner = this.add.rectangle(0, 14, 34, 9, isPlayer ? 0x2f6f45 : 0x7b2d32, 1).setStrokeStyle(1, 0x0b0b0b, 1);
        const icon = this.add.text(0, -4, String(army.units.length), {
          fontFamily: VisualTheme.fonts.display,
          fontSize: '15px',
          color: '#ffffff',
          stroke: '#000000',
          strokeThickness: 2,
        }).setOrigin(0.5);
        const strength = this.add.text(0, 15, this.compactNumber(army.units.reduce((sum, stack) => sum + stack.strength, 0)), {
          fontFamily: VisualTheme.fonts.body,
          fontSize: '9px',
          color: '#ffffff',
        }).setOrigin(0.5);
        const container = this.add.container(position.x + offsetX, position.y + offsetY, [ring, shield, banner, icon, strength])
          .setSize(56, 56)
          .setDepth(selected ? 35 : 30);
        container.setInteractive(new Phaser.Geom.Circle(0, 0, 29), Phaser.Geom.Circle.Contains);
        if (container.input) container.input.cursor = 'pointer';
        container.on('pointerover', (pointer: Phaser.Input.Pointer) => {
          container.setScale(1.1);
          this.showArmyTooltip(pointer, army.id);
        });
        container.on('pointermove', (pointer: Phaser.Input.Pointer) => this.moveTooltip(pointer));
        container.on('pointerout', () => {
          container.setScale(1);
          this.hideTooltip();
        });
        container.on('pointerdown', () => {
          campaign.selectedArmyId = army.id;
          campaign.selectedProvinceId = army.provinceId;
          this.render();
        });
      });
    }
  }

  private drawTopBar() {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[campaign.playerFactionId];
    const data = DataRegistry.instance.factions[campaign.playerFactionId];
    this.fixed(this.add.rectangle(800, 42, 1600, 84, VisualTheme.colors.night, 0.98).setStrokeStyle(2, VisualTheme.colors.gold, 0.95), 1000);
    this.fixed(this.add.rectangle(800, 80, 1568, 2, VisualTheme.colors.goldBright, 0.35), 1001);

    this.fixed(this.add.text(24, 11, `${data?.name ?? campaign.playerFactionId}`, {
      fontFamily: VisualTheme.fonts.display,
      fontSize: '25px',
      color: VisualTheme.text.title,
      stroke: '#120b07',
      strokeThickness: 2,
    }), 1002);
    this.fixed(this.add.text(24, 46, `${formatMonth(campaign.year, campaign.month)}  •  Turn ${campaign.turn}  •  ${campaign.activeOverlay.toUpperCase()} MAP`, {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '14px',
      color: VisualTheme.text.accent,
    }), 1002);

    const metrics = [
      ['Gold', faction.treasury, '#f4ce74'],
      ['Legitimacy', faction.legitimacy, '#eadfca'],
      ['Prestige', faction.prestige, '#9ed6b3'],
      ['War fatigue', faction.warExhaustion, faction.warExhaustion > 35 ? '#e78b82' : '#eadfca'],
    ] as const;
    metrics.forEach(([label, value, color], index) => {
      const x = 405 + index * 170;
      this.fixed(this.add.rectangle(x, 42, 152, 58, VisualTheme.colors.slate, 0.88).setStrokeStyle(1, VisualTheme.colors.gold, 0.35), 1001);
      this.fixed(this.add.text(x - 66, 20, label.toUpperCase(), {
        fontFamily: VisualTheme.fonts.body,
        fontSize: '10px',
        color: VisualTheme.text.muted,
      }), 1002);
      this.fixed(this.add.text(x - 66, 36, String(value), {
        fontFamily: VisualTheme.fonts.display,
        fontSize: '20px',
        color,
      }), 1002);
    });

    createTextButton(this, {
      label: this.nextMonthLabel(),
      x: 1455,
      y: 42,
      width: 255,
      height: 52,
      fontSize: 16,
      onClick: () => this.endMonthWithFeedback(),
    }).setScrollFactor(0).setDepth(1003);
  }

  private drawNavigation() {
    const buttons = [
      ['Court', SceneKeys.Court],
      ['Diplomacy', SceneKeys.Diplomacy],
      ['Trade', SceneKeys.Trade],
      ['Technology', SceneKeys.Tech],
      ['Espionage', SceneKeys.Espionage],
      ['Save / Load', SceneKeys.SaveLoad],
    ] as const;
    buttons.forEach(([label, scene], index) => {
      createTextButton(this, {
        label,
        x: 82 + index * 147,
        y: 111,
        width: 136,
        height: 36,
        fontSize: 14,
        onClick: () => this.scene.start(scene),
      }).setScrollFactor(0).setDepth(1100);
    });

    const campaign = GameState.instance.requireCampaign();
    MAP_MODES.forEach((mode, index) => {
      this.createMapModeButton(mode, 761 + index * 89, 111, campaign.activeOverlay === mode.id);
    });
    this.fixed(this.add.text(1092, 139, 'Keys 1–9 select map modes  •  M cycles modes', {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '11px',
      color: VisualTheme.text.muted,
    }).setOrigin(0.5), 1100);
  }

  private drawSidePanel() {
    const registry = DataRegistry.instance;
    const campaign = GameState.instance.requireCampaign();
    const selectedId = campaign.selectedProvinceId ?? Object.keys(campaign.provinces)[0];
    const province = registry.provinces[selectedId];
    const provinceState = campaign.provinces[selectedId];
    const panel = this.fixed(this.add.rectangle(1340, 493, 500, 660, VisualTheme.colors.night, 0.97).setStrokeStyle(3, VisualTheme.colors.gold, 0.95), 1200);
    panel.setInteractive();
    this.fixed(this.add.rectangle(1340, 177, 470, 1, VisualTheme.colors.goldBright, 0.34), 1201);
    if (!province || !provinceState) return;

    const owner = registry.factions[provinceState.owner];
    const ownerColor = Phaser.Display.Color.HexStringToColor(owner?.colors.primary ?? '#777777').color;
    this.fixed(this.add.rectangle(1106, 184, 8, 70, ownerColor, 1), 1202);
    this.fixed(this.add.text(1124, 166, province.name, {
      fontFamily: VisualTheme.fonts.display,
      fontSize: '31px',
      color: VisualTheme.text.title,
      stroke: '#100a06',
      strokeThickness: 2,
    }), 1202);
    this.fixed(this.add.text(1126, 205, `${owner?.name ?? provinceState.owner}  •  ${provinceState.culture}  •  ${provinceState.religion}`, {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '14px',
      color: VisualTheme.text.accent,
      wordWrap: { width: 430 },
    }), 1202);

    const metricValues = [
      ['Population', province.population, '#eadfca'],
      ['Prosperity', provinceState.prosperity, '#91cfa3'],
      ['Unrest', provinceState.unrest, provinceState.unrest > 20 ? '#e78b82' : '#eadfca'],
      ['Supply', SupplyManager.instance.provinceSupplyCapacity(selectedId), '#8fc8df'],
    ] as const;
    metricValues.forEach(([label, value, color], index) => this.drawMetricCard(1160 + index * 112, 278, label, value, color));

    this.fixed(this.add.text(1110, 322, `${province.terrain.toUpperCase()}  •  ${province.port ? `PORT ${provinceState.portLevel ?? 1}` : 'INLAND'}  •  FORT ${provinceState.fortLevel ?? 0}  •  ROAD ${provinceState.roadLevel ?? 0}`, {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '13px',
      color: VisualTheme.text.body,
    }), 1202);
    this.fixed(this.add.text(1110, 350, `Loyalty ${provinceState.loyalty}  •  Devastation ${provinceState.devastation}  •  Controller ${registry.factions[provinceState.controller]?.name ?? provinceState.controller}`, {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '13px',
      color: provinceState.controller !== provinceState.owner ? VisualTheme.text.danger : VisualTheme.text.muted,
      wordWrap: { width: 450 },
    }), 1202);

    this.fixed(this.add.text(1110, 392, 'Resources & stockpiles', {
      fontFamily: VisualTheme.fonts.display,
      fontSize: '19px',
      color: VisualTheme.text.title,
    }), 1202);
    const deposits = province.deposits.map((id) => registry.goods[id]?.name ?? id).join('  •  ') || 'No known deposits';
    const storage = Object.entries(provinceState.storage).slice(0, 6).map(([good, amount]) => `${registry.goods[good]?.name ?? good} ${amount}`).join('  •  ') || 'No stockpile';
    this.fixed(this.add.text(1110, 420, `${deposits}\n${storage}`, {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '13px',
      color: VisualTheme.text.body,
      wordWrap: { width: 450 },
      lineSpacing: 7,
    }), 1202);

    this.fixed(this.add.text(1110, 480, 'Buildings & construction', {
      fontFamily: VisualTheme.fonts.display,
      fontSize: '19px',
      color: VisualTheme.text.title,
    }), 1202);
    const buildings = provinceState.buildings.map((id) => registry.buildings[id]?.name ?? id).join(', ') || 'None';
    const queue = provinceState.constructionQueue.map((item) => `${registry.buildings[item.buildingId]?.name ?? item.buildingId} (${item.remainingMonths}m)`).join(', ') || 'No construction queued';
    this.fixed(this.add.text(1110, 508, `${buildings}\n${queue}`, {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '13px',
      color: VisualTheme.text.body,
      wordWrap: { width: 450 },
      lineSpacing: 7,
    }), 1202);

    createTextButton(this, {
      label: 'Open Province',
      x: 1215,
      y: 590,
      width: 210,
      height: 38,
      fontSize: 14,
      onClick: () => this.scene.start(SceneKeys.Province),
    }).setScrollFactor(0).setDepth(1203);
    createTextButton(this, {
      label: 'Build Paddies',
      x: 1455,
      y: 590,
      width: 210,
      height: 38,
      fontSize: 14,
      onClick: () => {
        ProvinceManager.instance.constructBuilding(selectedId, 'rice_paddies');
        this.render();
      },
    }).setScrollFactor(0).setDepth(1203);
    createTextButton(this, {
      label: 'Recruit Unit',
      x: 1215,
      y: 638,
      width: 210,
      height: 38,
      fontSize: 14,
      onClick: () => {
        this.recruitFirstAvailable(selectedId);
        this.render();
      },
    }).setScrollFactor(0).setDepth(1203);
    createTextButton(this, {
      label: 'Supply Depot',
      x: 1455,
      y: 638,
      width: 210,
      height: 38,
      fontSize: 14,
      onClick: () => {
        ProvinceManager.instance.setSupplyDepot(selectedId);
        this.render();
      },
    }).setScrollFactor(0).setDepth(1203);

    const army = campaign.selectedArmyId ? campaign.armies[campaign.selectedArmyId] : Object.values(campaign.armies).find((entry) => entry.factionId === campaign.playerFactionId);
    if (army) this.drawSelectedArmyPanel(army, selectedId);
  }

  private drawSelectedArmyPanel(army: ArmyState, selectedProvinceId: string) {
    const campaign = GameState.instance.requireCampaign();
    const registry = DataRegistry.instance;
    const manpower = army.units.reduce((sum, unit) => sum + unit.strength, 0);
    const destination = army.destinationProvinceId ? registry.provinces[army.destinationProvinceId] : undefined;
    const eta = ArmyManager.instance.estimatedMonths(army.id);
    this.fixed(this.add.rectangle(1340, 727, 462, 114, VisualTheme.colors.slate, 0.88).setStrokeStyle(1, VisualTheme.colors.gold, 0.5), 1202);
    this.fixed(this.add.text(1122, 684, `Selected army: ${army.id}`, {
      fontFamily: VisualTheme.fonts.display,
      fontSize: '17px',
      color: VisualTheme.text.title,
    }), 1203);
    this.fixed(this.add.text(1122, 710, `${army.units.length} units  •  ${this.compactNumber(manpower)} troops  •  ${registry.provinces[army.provinceId]?.name ?? army.provinceId}`, {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '13px',
      color: VisualTheme.text.body,
    }), 1203);
    this.fixed(this.add.text(1122, 734, destination ? `Marching to ${destination.name}  •  ETA ${eta} month${eta === 1 ? '' : 's'}` : 'No active march order', {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '13px',
      color: destination ? VisualTheme.text.accent : VisualTheme.text.muted,
    }), 1203);

    createTextButton(this, {
      label: 'Move Here',
      x: 1215,
      y: 785,
      width: 210,
      height: 36,
      fontSize: 14,
      onClick: () => {
        ArmyManager.instance.moveArmy(army.id, selectedProvinceId);
        this.render();
      },
    }).setScrollFactor(0).setDepth(1204);

    const provinceOwner = campaign.provinces[army.provinceId]?.owner;
    if (provinceOwner && provinceOwner !== army.factionId) {
      createTextButton(this, {
        label: 'Fight Battle',
        x: 1455,
        y: 785,
        width: 210,
        height: 36,
        fontSize: 14,
        onClick: () => this.scene.start(SceneKeys.Battle, {
          attackerFactionId: army.factionId,
          defenderFactionId: provinceOwner,
          provinceId: army.provinceId,
          attackerArmyIds: [army.id],
          defenderArmyIds: ArmyManager.instance.defendersForProvince(army.provinceId, army.factionId).map((entry) => entry.id),
        }),
      }).setScrollFactor(0).setDepth(1204);
    }
  }

  private drawNotifications() {
    const campaign = GameState.instance.requireCampaign();
    this.fixed(this.add.rectangle(530, 829, 1040, 122, VisualTheme.colors.night, 0.96).setStrokeStyle(2, VisualTheme.colors.gold, 0.72), 1200);
    this.fixed(this.add.text(28, 780, 'Campaign alerts', {
      fontFamily: VisualTheme.fonts.display,
      fontSize: '20px',
      color: VisualTheme.text.title,
    }), 1201);
    this.fixed(this.add.text(190, 786, `${campaign.log.filter((entry) => entry.level === 'danger' || entry.level === 'warning').length} priority`, {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '12px',
      color: VisualTheme.text.muted,
    }), 1201);

    const entries = campaign.log.slice(0, 4);
    if (!entries.length) {
      this.fixed(this.add.text(44, 824, 'No current notifications.', {
        fontFamily: VisualTheme.fonts.body,
        fontSize: '14px',
        color: VisualTheme.text.muted,
      }), 1201);
      return;
    }
    entries.forEach((entry, index) => {
      const x = 155 + index * 250;
      const color = this.levelColor(entry.level);
      this.fixed(this.add.rectangle(x, 837, 232, 72, VisualTheme.colors.slate, 0.9).setStrokeStyle(1, color, 0.85), 1201);
      this.fixed(this.add.rectangle(x - 113, 837, 5, 66, color, 1), 1202);
      this.fixed(this.add.text(x - 101, 808, `TURN ${entry.turn}  •  ${entry.level.toUpperCase()}`, {
        fontFamily: VisualTheme.fonts.body,
        fontSize: '9px',
        color: this.hexColor(color),
      }), 1202);
      this.fixed(this.add.text(x - 101, 826, this.truncate(entry.text, 76), {
        fontFamily: VisualTheme.fonts.body,
        fontSize: '12px',
        color: VisualTheme.text.body,
        wordWrap: { width: 204 },
        lineSpacing: 2,
      }), 1202);
    });
  }

  private drawLegend() {
    const campaign = GameState.instance.requireCampaign();
    const mode = MAP_MODES.find((entry) => entry.id === campaign.activeOverlay) ?? MAP_MODES[0];
    this.fixed(this.add.rectangle(280, 695, 500, 92, VisualTheme.colors.night, 0.9).setStrokeStyle(2, VisualTheme.colors.gold, 0.58), 1150);
    this.fixed(this.add.text(46, 660, `${mode.label} map`, {
      fontFamily: VisualTheme.fonts.display,
      fontSize: '17px',
      color: VisualTheme.text.title,
    }), 1151);
    this.fixed(this.add.text(46, 683, mode.description, {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '11px',
      color: VisualTheme.text.muted,
      wordWrap: { width: 450 },
    }), 1151);
    this.legendEntries(campaign.activeOverlay).forEach(([label, color], index) => {
      const x = 72 + index * 113;
      this.fixed(this.add.rectangle(x, 721, 14, 14, color, 1).setStrokeStyle(1, 0xffffff, 0.45), 1152);
      this.fixed(this.add.text(x + 13, 714, label, {
        fontFamily: VisualTheme.fonts.body,
        fontSize: '10px',
        color: VisualTheme.text.body,
      }), 1152);
    });
  }

  private drawTurnSummary() {
    if (!this.turnSummary) return;
    const color = this.levelColor(this.turnSummary.level);
    const panel = this.fixed(this.add.container(800, 176), 3000);
    const background = this.add.rectangle(0, 0, 520, 74, VisualTheme.colors.night, 0.98).setStrokeStyle(3, color, 1);
    const title = this.add.text(-236, -25, this.turnSummary.title, {
      fontFamily: VisualTheme.fonts.display,
      fontSize: '20px',
      color: VisualTheme.text.title,
    });
    const detail = this.add.text(-236, 6, this.turnSummary.detail, {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '13px',
      color: VisualTheme.text.body,
      wordWrap: { width: 470 },
    });
    panel.add([background, title, detail]);
    panel.setAlpha(0).setY(160);
    this.tweens.add({ targets: panel, alpha: 1, y: 176, duration: 180, ease: 'Sine.Out' });
  }

  private createMapModeButton(mode: { id: CampaignMapMode; label: string }, x: number, y: number, active: boolean) {
    const background = this.add.rectangle(0, 0, 82, 34, active ? VisualTheme.colors.jade : VisualTheme.colors.panel, 0.98)
      .setStrokeStyle(2, active ? VisualTheme.colors.goldBright : VisualTheme.colors.gold, active ? 1 : 0.55);
    const label = this.add.text(0, 0, mode.label, {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '11px',
      color: active ? '#fff2cf' : VisualTheme.text.body,
    }).setOrigin(0.5);
    const container = this.add.container(x, y, [background, label]).setSize(82, 34).setScrollFactor(0).setDepth(1100);
    container.setInteractive(new Phaser.Geom.Rectangle(-41, -17, 82, 34), Phaser.Geom.Rectangle.Contains);
    if (container.input) container.input.cursor = 'pointer';
    container.on('pointerover', () => {
      background.setFillStyle(active ? VisualTheme.colors.jadeBright : VisualTheme.colors.panelHover, 1);
      background.setStrokeStyle(2, VisualTheme.colors.goldBright, 1);
    });
    container.on('pointerout', () => {
      background.setFillStyle(active ? VisualTheme.colors.jade : VisualTheme.colors.panel, 0.98);
      background.setStrokeStyle(2, active ? VisualTheme.colors.goldBright : VisualTheme.colors.gold, active ? 1 : 0.55);
    });
    container.on('pointerdown', () => {
      GameState.instance.requireCampaign().activeOverlay = mode.id;
      AudioManager.instance.playUi(this);
      this.render();
    });
  }

  private drawMetricCard(x: number, y: number, label: string, value: string | number, color: string) {
    this.fixed(this.add.rectangle(x, y, 103, 62, VisualTheme.colors.slate, 0.9).setStrokeStyle(1, VisualTheme.colors.gold, 0.38), 1202);
    this.fixed(this.add.text(x - 43, y - 23, label.toUpperCase(), {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '9px',
      color: VisualTheme.text.muted,
    }), 1203);
    this.fixed(this.add.text(x - 43, y - 5, String(value), {
      fontFamily: VisualTheme.fonts.display,
      fontSize: '19px',
      color,
    }), 1203);
  }

  private endMonthWithFeedback() {
    if (this.endingTurn) return;
    this.endingTurn = true;
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[campaign.playerFactionId];
    const beforeTreasury = faction.treasury;
    const beforeDate = formatMonth(campaign.year, campaign.month);
    TurnManager.instance.endMonth();
    SaveManager.instance.save();
    const treasuryDelta = faction.treasury - beforeTreasury;
    const marching = Object.values(campaign.armies).filter((army) => army.factionId === campaign.playerFactionId && army.destinationProvinceId).length;
    this.turnSummary = {
      title: `${beforeDate} concluded — ${formatMonth(campaign.year, campaign.month)} begins`,
      detail: `Treasury ${treasuryDelta >= 0 ? '+' : ''}${treasuryDelta}  •  ${campaign.recruitmentQueue.length} recruits training  •  ${marching} armies marching`,
      level: treasuryDelta < 0 ? 'warning' : 'success',
    };
    this.endingTurn = false;
    this.render();

    if (EventManager.instance.pendingEvent) {
      this.time.delayedCall(420, () => {
        if (this.scene.isActive()) this.scene.start(SceneKeys.Event);
      });
    } else {
      this.time.delayedCall(1900, () => {
        if (!this.scene.isActive()) return;
        this.turnSummary = undefined;
        this.render();
      });
    }
  }

  private cycleMapMode() {
    const campaign = GameState.instance.requireCampaign();
    const index = MAP_MODES.findIndex((mode) => mode.id === campaign.activeOverlay);
    campaign.activeOverlay = MAP_MODES[(index + 1) % MAP_MODES.length].id;
    this.render();
  }

  private nextMonthLabel() {
    const campaign = GameState.instance.requireCampaign();
    const month = campaign.month === 12 ? 1 : campaign.month + 1;
    const year = campaign.month === 12 ? campaign.year + 1 : campaign.year;
    return `Advance to ${formatMonth(year, month)}`;
  }

  private overlayColor(provinceId: string) {
    const registry = DataRegistry.instance;
    const campaign = GameState.instance.requireCampaign();
    const province = registry.provinces[provinceId];
    const state = campaign.provinces[provinceId];
    if (!province || !state) return 0x777777;

    switch (campaign.activeOverlay) {
      case 'terrain':
        return this.terrainColor(province.terrain);
      case 'religion':
        return Phaser.Display.Color.HexStringToColor(state.religion === 'Islam' ? '#257347' : state.religion.includes('Hindu') ? '#d09a28' : state.religion.includes('Catholic') ? '#8f3f46' : '#6174a3').color;
      case 'culture':
        return Phaser.Display.Color.HSLToColor((this.hashString(state.culture) % 360) / 360, 0.46, 0.48).color;
      case 'trade':
        if (province.port && province.deposits.includes('spices')) return 0xd6a33b;
        if (province.port) return 0x238397;
        if (province.deposits.length > 1) return 0x637d54;
        return 0x4d5663;
      case 'unrest':
        return state.unrest >= 35 ? 0x9e3035 : state.unrest >= 18 ? 0xc9792f : state.unrest >= 8 ? 0xb4a343 : 0x35794f;
      case 'supply': {
        const supply = SupplyManager.instance.provinceSupplyCapacity(provinceId);
        return supply >= 80 ? 0x377d62 : supply >= 50 ? 0x4e8fa1 : supply >= 25 ? 0x9a8948 : 0x8a4145;
      }
      case 'development': {
        const development = Math.round((province.population + state.prosperity) / 2);
        return development >= 80 ? 0xd6a33b : development >= 60 ? 0x6c9250 : development >= 40 ? 0x477b68 : 0x46505c;
      }
      case 'devastation':
        return state.devastation >= 60 ? 0x4e1f24 : state.devastation >= 30 ? 0x8a3a32 : state.devastation >= 10 ? 0xb97737 : 0x3c7351;
      default:
        return Phaser.Display.Color.HexStringToColor(registry.factions[state.owner]?.colors.primary ?? '#777777').color;
    }
  }

  private provinceModeValue(provinceId: string) {
    const registry = DataRegistry.instance;
    const campaign = GameState.instance.requireCampaign();
    const province = registry.provinces[provinceId];
    const state = campaign.provinces[provinceId];
    if (!province || !state) return '';
    switch (campaign.activeOverlay) {
      case 'political': return (registry.factions[state.owner]?.name ?? state.owner).slice(0, 3).toUpperCase();
      case 'terrain': return province.terrain.slice(0, 5).toUpperCase();
      case 'religion': return state.religion.slice(0, 4).toUpperCase();
      case 'culture': return state.culture.slice(0, 4).toUpperCase();
      case 'trade': return province.port ? `PORT ${province.deposits.length}` : `${province.deposits.length} GOODS`;
      case 'unrest': return `U ${state.unrest}`;
      case 'supply': return `S ${SupplyManager.instance.provinceSupplyCapacity(provinceId)}`;
      case 'development': return `D ${Math.round((province.population + state.prosperity) / 2)}`;
      case 'devastation': return `DMG ${state.devastation}`;
    }
  }

  private legendEntries(mode: CampaignMapMode): Array<[string, number]> {
    switch (mode) {
      case 'terrain': return [['Plains', 0x6f8b4a], ['Coast', 0x3e8090], ['Highland', 0x85714d], ['Forest', 0x3d6942]];
      case 'religion': return [['Islam', 0x257347], ['Hindu', 0xd09a28], ['Catholic', 0x8f3f46], ['Other', 0x6174a3]];
      case 'trade': return [['Spice port', 0xd6a33b], ['Port', 0x238397], ['Producer', 0x637d54], ['Local', 0x4d5663]];
      case 'unrest': return [['Stable', 0x35794f], ['Concern', 0xb4a343], ['High', 0xc9792f], ['Revolt', 0x9e3035]];
      case 'supply': return [['Excellent', 0x377d62], ['Good', 0x4e8fa1], ['Limited', 0x9a8948], ['Poor', 0x8a4145]];
      case 'development': return [['Capital', 0xd6a33b], ['Developed', 0x6c9250], ['Growing', 0x477b68], ['Rural', 0x46505c]];
      case 'devastation': return [['Peaceful', 0x3c7351], ['Damaged', 0xb97737], ['Ruined', 0x8a3a32], ['Wasteland', 0x4e1f24]];
      case 'culture': return [['Distinct colors', 0x7e6bb5], ['show regional', 0x4f8f7b], ['cultural groups', 0xb67b4d], ['at a glance', 0x56809c]];
      default: return [['Your realm', 0x8e1f1f], ['Other realms', 0x426b89], ['Selected', VisualTheme.colors.goldBright], ['Occupied', 0x7e4a45]];
    }
  }

  private terrainColor(terrain: string) {
    const normalized = terrain.toLowerCase();
    if (normalized.includes('coast')) return 0x3e8090;
    if (normalized.includes('high') || normalized.includes('mountain')) return 0x85714d;
    if (normalized.includes('forest') || normalized.includes('jungle')) return 0x3d6942;
    if (normalized.includes('island')) return 0x4c8a75;
    return 0x6f8b4a;
  }

  private recruitFirstAvailable(provinceId: string) {
    const campaign = GameState.instance.requireCampaign();
    const roster = DataRegistry.instance.factions[campaign.playerFactionId]?.roster ?? [];
    const firstAffordable = roster.find((unitId) => {
      const unit = DataRegistry.instance.units[unitId];
      return unit && campaign.factions[campaign.playerFactionId].treasury >= unit.costGold;
    });
    if (firstAffordable) RecruitmentManager.instance.recruit(campaign.playerFactionId, provinceId, firstAffordable);
    else GameState.instance.log('No affordable roster unit is available.', 'warning');
  }

  private moveSelectedArmy(provinceId: string) {
    const campaign = GameState.instance.requireCampaign();
    if (!campaign.selectedArmyId) {
      GameState.instance.log('Select an army before issuing a move order.', 'warning');
      this.render();
      return;
    }
    ArmyManager.instance.moveArmy(campaign.selectedArmyId, provinceId);
    this.render();
  }

  private showProvinceTooltip(pointer: Phaser.Input.Pointer, provinceId: string) {
    const registry = DataRegistry.instance;
    const campaign = GameState.instance.requireCampaign();
    const province = registry.provinces[provinceId];
    const state = campaign.provinces[provinceId];
    if (!province || !state) return;
    const owner = registry.factions[state.owner];
    const content = [
      province.name,
      `${owner?.name ?? state.owner}  •  ${province.terrain}`,
      `${state.culture}  •  ${state.religion}`,
      `Faith tension ${Math.round(state.religiousTension)}  •  Culture tension ${Math.round(state.culturalTension)}`,
      `Population ${province.population}  Prosperity ${state.prosperity}`,
      `Unrest ${state.unrest}  Supply ${SupplyManager.instance.provinceSupplyCapacity(provinceId)}  Damage ${state.devastation}`,
      'Left click selects  •  Double-click opens  •  Right-click marches',
    ];
    this.createTooltip(pointer, content, owner ? Phaser.Display.Color.HexStringToColor(owner.colors.primary).color : VisualTheme.colors.gold);
  }

  private showArmyTooltip(pointer: Phaser.Input.Pointer, armyId: string) {
    const campaign = GameState.instance.requireCampaign();
    const registry = DataRegistry.instance;
    const army = campaign.armies[armyId];
    if (!army) return;
    const faction = registry.factions[army.factionId];
    const manpower = army.units.reduce((sum, stack) => sum + stack.strength, 0);
    const destination = army.destinationProvinceId ? registry.provinces[army.destinationProvinceId] : undefined;
    const content = [
      army.id,
      `${faction?.name ?? army.factionId}  •  ${army.stance.toUpperCase()}`,
      `${army.units.length} units  •  ${this.compactNumber(manpower)} troops`,
      `Location: ${registry.provinces[army.provinceId]?.name ?? army.provinceId}`,
      destination ? `Destination: ${destination.name}  •  ETA ${ArmyManager.instance.estimatedMonths(army.id)}m` : 'No active march order',
      army.factionId === campaign.playerFactionId ? 'Click to select this army' : 'Foreign army',
    ];
    this.createTooltip(pointer, content, faction ? Phaser.Display.Color.HexStringToColor(faction.colors.primary).color : 0x777777);
  }

  private createTooltip(pointer: Phaser.Input.Pointer, lines: string[], accent: number) {
    this.hideTooltip();
    const width = 330;
    const height = 142;
    const x = Phaser.Math.Clamp(pointer.x + 18, 12, this.scale.width - width - 12);
    const y = Phaser.Math.Clamp(pointer.y + 18, 12, this.scale.height - height - 12);
    const background = this.add.rectangle(0, 0, width, height, VisualTheme.colors.night, 0.98)
      .setOrigin(0)
      .setStrokeStyle(2, accent, 1);
    const strip = this.add.rectangle(0, 0, 6, height, accent, 1).setOrigin(0);
    const title = this.add.text(16, 10, lines[0], {
      fontFamily: VisualTheme.fonts.display,
      fontSize: '18px',
      color: VisualTheme.text.title,
    });
    const body = this.add.text(16, 37, lines.slice(1).join('\n'), {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '12px',
      color: VisualTheme.text.body,
      lineSpacing: 4,
      wordWrap: { width: width - 30 },
    });
    this.tooltip = this.add.container(x, y, [background, strip, title, body]).setScrollFactor(0).setDepth(4000);
  }

  private moveTooltip(pointer: Phaser.Input.Pointer) {
    if (!this.tooltip) return;
    const width = 330;
    const height = 142;
    this.tooltip.setPosition(
      Phaser.Math.Clamp(pointer.x + 18, 12, this.scale.width - width - 12),
      Phaser.Math.Clamp(pointer.y + 18, 12, this.scale.height - height - 12),
    );
  }

  private hideTooltip() {
    this.tooltip?.destroy(true);
    this.tooltip = undefined;
  }

  private projectProvince(province: ProvinceData) {
    const bounds = this.getProjectionBounds();
    const x = Phaser.Math.Linear(86, 1016, (province.x - bounds.minX) / Math.max(1, bounds.maxX - bounds.minX));
    const y = Phaser.Math.Linear(193, 681, (province.y - bounds.minY) / Math.max(1, bounds.maxY - bounds.minY));
    return { x, y };
  }

  private getProjectionBounds() {
    if (this.projectionBounds) return this.projectionBounds;
    const provinces = DataRegistry.instance.provinceList();
    this.projectionBounds = {
      minX: Math.min(...provinces.map((province) => province.x)) - 35,
      maxX: Math.max(...provinces.map((province) => province.x)) + 35,
      minY: Math.min(...provinces.map((province) => province.y)) - 30,
      maxY: Math.max(...provinces.map((province) => province.y)) + 30,
    };
    return this.projectionBounds;
  }

  private provinceRadius(province: ProvinceData) {
    const center = this.projectProvince(province);
    const distances = province.neighbors
      .map((neighborId) => DataRegistry.instance.provinces[neighborId])
      .filter((neighbor): neighbor is ProvinceData => Boolean(neighbor))
      .map((neighbor) => {
        const projected = this.projectProvince(neighbor);
        return Phaser.Math.Distance.Between(center.x, center.y, projected.x, projected.y);
      });
    const nearest = distances.length ? Math.min(...distances) : 130;
    return Phaser.Math.Clamp(nearest * 0.29, 35, province.port ? 55 : 50);
  }

  private provincePolygon(id: string, radius: number) {
    const seed = this.hashString(id);
    const points: Phaser.Math.Vector2[] = [];
    for (let index = 0; index < 9; index++) {
      const angle = -Math.PI / 2 + index * Math.PI * 2 / 9;
      const jitter = 0.84 + ((seed >> (index % 12)) & 7) / 32;
      const horizontal = radius * 1.18;
      points.push(new Phaser.Math.Vector2(Math.cos(angle) * horizontal * jitter, Math.sin(angle) * radius * jitter));
    }
    return points;
  }

  private drawDashedLine(graphics: Phaser.GameObjects.Graphics, x1: number, y1: number, x2: number, y2: number, dash: number, gap: number) {
    const distance = Phaser.Math.Distance.Between(x1, y1, x2, y2);
    const angle = Phaser.Math.Angle.Between(x1, y1, x2, y2);
    for (let traveled = 0; traveled < distance; traveled += dash + gap) {
      const end = Math.min(distance, traveled + dash);
      graphics.lineBetween(
        x1 + Math.cos(angle) * traveled,
        y1 + Math.sin(angle) * traveled,
        x1 + Math.cos(angle) * end,
        y1 + Math.sin(angle) * end,
      );
    }
  }

  private brightenColor(color: number, amount: number) {
    const red = Math.min(255, ((color >> 16) & 255) + amount);
    const green = Math.min(255, ((color >> 8) & 255) + amount);
    const blue = Math.min(255, (color & 255) + amount);
    return Phaser.Display.Color.GetColor(red, green, blue);
  }

  private levelColor(level: GameLogEntry['level']) {
    if (level === 'danger') return 0xc84b4b;
    if (level === 'warning') return 0xd8a13b;
    if (level === 'success') return 0x5ca877;
    return 0x5d89a1;
  }

  private hexColor(color: number) {
    return `#${color.toString(16).padStart(6, '0')}`;
  }

  private hashString(value: string) {
    let hash = 2166136261;
    for (let index = 0; index < value.length; index++) {
      hash ^= value.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
  }

  private compactNumber(value: number) {
    if (value >= 10000) return `${Math.round(value / 1000)}k`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
    return String(Math.round(value));
  }

  private truncate(value: string, length: number) {
    return value.length > length ? `${value.slice(0, length - 1)}…` : value;
  }

  private fixed<T extends Phaser.GameObjects.GameObject & { setScrollFactor: (x: number, y?: number) => T; setDepth: (value: number) => T }>(object: T, depth: number) {
    return object.setScrollFactor(0).setDepth(depth);
  }
}
